/**
 * NewsletterSubscribersPage — Admin, READ-ONLY.
 *
 * Expose la collection existante `newsletter_subscribers` via
 * `GET /api/admin/newsletter/subscribers` (champs techniques déjà projetés
 * côté backend : confirmation_token_hash / unsubscribe_token_hash / ip_hash /
 * user_agent_hash sont EXCLUS avant retour). Actions admin : renvoi de confirmation
 * (pending) et suppression de l'inscription newsletter uniquement.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { NewsletterRowActions } from "../components/admin/NewsletterRowActions";
import { Loader2, RefreshCw, Download, Mail, CheckCircle2, Clock, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_LABEL = {
  pending: "En attente",
  confirmed: "Confirmé",
  unsubscribed: "Désinscrit",
};

const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  unsubscribed: "bg-slate-200 text-slate-700",
};

// Champs techniques privés — jamais affichés ni exportés, double sécurité
// (le backend les filtre déjà, on filtre une seconde fois côté UI).
const PRIVATE_FIELDS = new Set([
  "confirmation_token_hash",
  "unsubscribe_token_hash",
  "ip_hash",
  "user_agent_hash",
]);

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function toCsvRow(cells) {
  return cells
    .map((v) => {
      const s = v == null ? "" : String(v);
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    })
    .join(";");
}

export default function NewsletterSubscribersPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, confirmed: 0, unsubscribed: 0 });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/newsletter/subscribers`, { params: { limit: 500 } });
      const safe = (r.data?.items || []).map((it) => {
        const out = {};
        for (const [k, v] of Object.entries(it)) if (!PRIVATE_FIELDS.has(k)) out[k] = v;
        return out;
      });
      setItems(safe);
      setCounts(r.data?.counts || { pending: 0, confirmed: 0, unsubscribed: 0 });
    } catch (e) {
      toast.error("Chargement échoué : " + (e?.response?.data?.detail || e?.message || "inconnue"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return items.filter((it) => {
      if (statusFilter !== "all" && it.status !== statusFilter) return false;
      if (qq && !(it.email || "").toLowerCase().includes(qq)) return false;
      return true;
    });
  }, [items, q, statusFilter]);

  const exportCsv = () => {
    const headers = ["email", "status", "source", "created_at", "confirmed_at", "unsubscribed_at"];
    const lines = [headers.join(";"), ...filtered.map((it) =>
      toCsvRow(headers.map((h) => h.endsWith("_at") ? fmtDate(it[h]) : (it[h] ?? "")))
    )];
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const ts = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 16);
    a.href = url;
    a.download = `itercatus-newsletter-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`${filtered.length} abonnés exportés.`);
  };

  return (
    <div className="p-6 space-y-5" data-testid="newsletter-admin-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary" />
          <h1 className="font-heading text-xl text-primary">Abonnés newsletter</h1>
                  </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="newsletter-refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          <Button size="sm" onClick={exportCsv} disabled={!filtered.length} data-testid="newsletter-export-csv-btn">
            <Download className="h-4 w-4 mr-2" /> Exporter CSV ({filtered.length})
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="border rounded-md p-3 bg-emerald-50/50" data-testid="counter-confirmed">
          <div className="text-[11px] uppercase tracking-wide text-emerald-800 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Confirmés</div>
          <div className="text-2xl font-semibold text-emerald-900">{counts.confirmed}</div>
        </div>
        <div className="border rounded-md p-3 bg-amber-50/50" data-testid="counter-pending">
          <div className="text-[11px] uppercase tracking-wide text-amber-800 flex items-center gap-1"><Clock className="h-3 w-3" /> En attente</div>
          <div className="text-2xl font-semibold text-amber-900">{counts.pending}</div>
        </div>
        <div className="border rounded-md p-3 bg-slate-50" data-testid="counter-unsubscribed">
          <div className="text-[11px] uppercase tracking-wide text-slate-700 flex items-center gap-1"><XCircle className="h-3 w-3" /> Désinscrits</div>
          <div className="text-2xl font-semibold text-slate-800">{counts.unsubscribed}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Input
          placeholder="Rechercher par email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm"
          data-testid="newsletter-search-input"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="newsletter-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="confirmed">Confirmés</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="unsubscribed">Désinscrits</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="newsletter-table">
          <thead className="bg-muted/40 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Email</th>
              <th className="px-3 py-2 font-medium">Date d'inscription</th>
              <th className="px-3 py-2 font-medium">Statut</th>
              <th className="px-3 py-2 font-medium">Source</th>
              <th className="px-3 py-2 font-medium">Date confirmation</th>
              <th className="px-3 py-2 font-medium">Date désinscription</th>
              <th className="px-3 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                {loading ? "Chargement…" : "Aucun abonné pour ces filtres."}
              </td></tr>
            ) : filtered.map((it, i) => (
              <tr key={it.email || i} className="border-t hover:bg-muted/30" data-testid={`newsletter-row-${i}`}>
                <td className="px-3 py-2 font-mono text-xs">{it.email}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(it.created_at)}</td>
                <td className="px-3 py-2">
                  <span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_COLOR[it.status] || "bg-gray-100 text-gray-700"}`}>
                    {STATUS_LABEL[it.status] || it.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">{it.source || "—"}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(it.confirmed_at)}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(it.unsubscribed_at)}</td>
                <td className="px-3 py-2"><NewsletterRowActions item={it} index={i} onChanged={load} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-muted-foreground">
        Les champs techniques privés (tokens hashés, hash IP, hash user-agent) ne sont jamais affichés ni exportés.
      </p>
    </div>
  );
}
